// TemplateFormModal.tsx — create or edit a proposal template.
// Admin-only: it is only opened from the admin Proposals page, and the API
// refuses anyone else.
//
// A template is: a name, ONE category, a channel (email / whatsapp), a subject
// (email only), a body with {{placeholders}}, up to 5 files and an active flag.
// Files go up as multipart (`attachments`); on edit, files to drop are sent as
// `remove_attachments` (their `path`).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, FileText, Mail, MessageCircle, Paperclip, Pencil, Plus, Trash2, Upload, X } from 'lucide-react';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import ProposalDialog, { FIELD_LABEL, Notice } from './ProposalDialog';
import { FileAttachment, ProposalCategory, ProposalChannel, ProposalTemplate, apiError, extractVariables, fmtSize, proposalApi } from './proposalTypes';

// Keep in sync with controllers/proposalTemplateController.go.
const NAME_MAX = 120;
const SUBJECT_MAX = 200;
const BODY_MAX = 10000;
const MAX_FILES = 5;
// The server has the final say on size / type (helpers/r2.go); this only saves a round trip.
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.doc,.docx';
const ALLOWED_EXT = ACCEPT.split(',').map((x) => x.slice(1));

const CHANNELS: { value: ProposalChannel; label: string; hint: string; icon: typeof Mail; color: string }[] = [
  { value: 'email', label: 'Email', hint: 'Sent by the server', icon: Mail, color: C.teal },
  { value: 'whatsapp', label: 'WhatsApp', hint: 'Opens a chat to send', icon: MessageCircle, color: '#128c4a' },
];

const controlBox = (invalid = false, disabled = false): React.CSSProperties => ({
  width: '100%',
  boxSizing: 'border-box',
  background: disabled ? '#f9fafb' : C.white,
  border: `1px solid ${invalid ? 'rgba(248,113,113,0.9)' : G.line}`,
  borderRadius: '8.3px',
  fontSize: '0.79rem',
  color: '#111827',
  fontFamily: 'inherit',
  outline: 'none',
});

interface TemplateFormModalProps {
  visible: boolean;
  /** null → create, a template → edit */
  template: ProposalTemplate | null;
  categories: ProposalCategory[];
  /** pre-selected category when creating (the one currently filtered on) */
  defaultCategoryId: string | null;
  /** placeholders the server fills itself — offered as insert chips */
  autoVariables: string[];
  onHide: () => void;
  onSaved: (t: ProposalTemplate) => void;
}

const TemplateFormModal: React.FC<TemplateFormModalProps> = ({ visible, template, categories, defaultCategoryId, autoVariables, onHide, onSaved }) => {
  const isEdit = !!template;

  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [channel, setChannel] = useState<ProposalChannel>('email');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [existing, setExisting] = useState<FileAttachment[]>([]); // files already on the template
  const [removed, setRemoved] = useState<string[]>([]); // paths of existing files to drop
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // ── Reset every time the modal opens ───────────────────────────────────────
  useEffect(() => {
    if (!visible) return;
    setName(template?.name ?? '');
    setCategoryId(template?.category_id ?? (defaultCategoryId && categories.some((c) => c.id === defaultCategoryId) ? defaultCategoryId : categories[0]?.id ?? ''));
    setChannel(template?.channel ?? 'email');
    setSubject(template?.subject ?? '');
    setBody(template?.body ?? '');
    setIsActive(template?.is_active ?? true);
    setExisting(template?.attachments ?? []);
    setRemoved([]);
    setNewFiles([]);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, template]);

  const isEmail = channel === 'email';
  const keptExisting = existing.filter((a) => !removed.includes(a.path));
  const fileCount = keptExisting.length + newFiles.length;

  // placeholders found in the text right now
  const found = useMemo(() => extractVariables(isEmail ? subject : '', body), [isEmail, subject, body]);
  const asked = found.filter((v) => !autoVariables.includes(v));

  // ── Body: insert a {{placeholder}} at the cursor ───────────────────────────
  const insertVar = (v: string) => {
    const token = `{{${v}}}`;
    const el = bodyRef.current;
    if (!el) {
      setBody((b) => b + token);
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + token + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  // ── Files ──────────────────────────────────────────────────────────────────
  const addFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const picked = Array.from(list);
    if (fileRef.current) fileRef.current.value = ''; // allow picking the same file again

    const room = MAX_FILES - fileCount;
    if (room <= 0) return setError(`A template can have at most ${MAX_FILES} files.`);

    const ok: File[] = [];
    for (const f of picked) {
      const ext = f.name.split('.').pop()?.toLowerCase() ?? '';
      if (!ALLOWED_EXT.includes(ext)) return setError(`"${f.name}" is not a supported file. Use PDF, image or Word files.`);
      if (f.size > MAX_FILE_BYTES) return setError(`"${f.name}" is larger than ${MAX_FILE_BYTES / 1024 / 1024} MB.`);
      ok.push(f);
    }
    if (ok.length > room) {
      setError(`A template can have at most ${MAX_FILES} files — only the first ${room} were added.`);
      setNewFiles((cur) => [...cur, ...ok.slice(0, room)]);
      return;
    }
    setError(null);
    setNewFiles((cur) => [...cur, ...ok]);
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const submit = async () => {
    const n = name.trim().replace(/\s+/g, ' ');
    const s = subject.trim().replace(/\s+/g, ' ');
    const b = body.replace(/\r\n/g, '\n').trim();

    if (!n) return setError('Give the template a name.');
    if (n.length > NAME_MAX) return setError(`The name can be at most ${NAME_MAX} characters.`);
    if (!categoryId) return setError('Pick a category.');
    if (isEmail && !s) return setError('An email template needs a subject.');
    if (isEmail && s.length > SUBJECT_MAX) return setError(`The subject can be at most ${SUBJECT_MAX} characters.`);
    if (!b) return setError('Write the message body.');
    if (b.length > BODY_MAX) return setError(`The body can be at most ${BODY_MAX} characters.`);
    if (fileCount > MAX_FILES) return setError(`A template can have at most ${MAX_FILES} files.`);

    const form = new FormData();
    form.append('name', n);
    form.append('category_id', categoryId);
    form.append('channel', channel);
    form.append('subject', isEmail ? s : '');
    form.append('body', b);
    form.append('is_active', String(isActive));
    removed.forEach((p) => form.append('remove_attachments', p));
    newFiles.forEach((f) => form.append('attachments', f));

    setSaving(true);
    setError(null);
    try {
      const res = isEdit && template ? await proposalApi.templates.update(template.id, form) : await proposalApi.templates.create(form);
      onSaved(res.data);
      onHide();
    } catch (e) {
      setError(apiError(e, isEdit ? 'Could not update the template.' : 'Could not create the template.'));
    } finally {
      setSaving(false);
    }
  };

  const noCategories = categories.length === 0;

  return (
    <ProposalDialog
      visible={visible}
      onHide={onHide}
      title={isEdit ? 'Edit template' : 'New template'}
      subtitle={isEdit ? 'Changes only affect proposals sent from now on' : 'Write it once — your team sends it from any lead'}
      icon={isEdit ? Pencil : FileText}
      busy={saving}
      maxWidth="680px"
      footer={
        <>
          <GlobalButton label="Cancel" variant="secondary" icon={X} onClick={onHide} disabled={saving} />
          <GlobalButton label={isEdit ? 'SAVE CHANGES' : 'CREATE TEMPLATE'} icon={Check} onClick={submit} loading={saving} disabled={noCategories} />
        </>
      }
    >
      {error && <Notice tone="error">{error}</Notice>}
      {noCategories && <Notice tone="warn">Create a category first — every template belongs to one.</Notice>}

      <GlobalInputField
        label="Name"
        required
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. MBBS admission offer"
        maxLength={NAME_MAX}
        disabled={saving}
        inputProps={{ autoFocus: true }}
      />

      {/* category + channel */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
        <div>
          <label style={FIELD_LABEL}>Category *</label>
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            disabled={saving || noCategories}
            style={{ ...controlBox(false, saving || noCategories), height: '40px', padding: '0 10px', cursor: saving ? 'not-allowed' : 'pointer' }}
          >
            {noCategories && <option value="">No categories</option>}
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={FIELD_LABEL}>Channel *</label>
          <div style={{ display: 'flex', gap: '7px' }}>
            {CHANNELS.map((ch) => {
              const on = channel === ch.value;
              const Icon = ch.icon;
              return (
                <button
                  key={ch.value}
                  type="button"
                  onClick={() => setChannel(ch.value)}
                  disabled={saving}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '7px',
                    height: '40px',
                    padding: '0 10px',
                    borderRadius: '8.3px',
                    border: `1.5px solid ${on ? ch.color : G.line}`,
                    background: on ? 'rgba(17,85,80,0.05)' : C.white,
                    cursor: saving ? 'not-allowed' : 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <Icon size={14} color={ch.color} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '0.74rem', fontWeight: 700, color: C.teal, lineHeight: 1.1 }}>{ch.label}</span>
                    <span style={{ display: 'block', fontSize: '0.58rem', color: C.muted, lineHeight: 1.2 }}>{ch.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {isEmail && (
        <GlobalInputField
          label="Subject"
          required
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="e.g. Your admission offer, {{lead_name}}"
          maxLength={SUBJECT_MAX}
          hint="Placeholders work here too."
          disabled={saving}
        />
      )}

      {/* body */}
      <div>
        <label style={{ ...FIELD_LABEL, display: 'flex', justifyContent: 'space-between' }}>
          <span>Message *</span>
          <span style={{ color: body.length > BODY_MAX ? '#c62828' : C.muted, fontWeight: 600, letterSpacing: 0, textTransform: 'none' }}>
            {body.length}/{BODY_MAX}
          </span>
        </label>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '5px', marginBottom: '7px' }}>
          <span style={{ fontSize: '0.62rem', color: C.muted, marginRight: '2px' }}>Insert</span>
          {autoVariables.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => insertVar(v)}
              disabled={saving}
              title={`Adds {{${v}}} where the cursor is`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '2px 8px', borderRadius: '20px', border: `1px solid ${C.limeBorder}`, background: C.limeDim, color: '#4a5e00', fontSize: '0.63rem', fontWeight: 700, cursor: 'pointer' }}
            >
              <Plus size={9} strokeWidth={3} /> {v}
            </button>
          ))}
        </div>

        <textarea
          ref={bodyRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={9}
          disabled={saving}
          placeholder={'Hi {{lead_name}},\n\nThank you for your interest in {{interested_in}}. …\n\n{{staff_name}}\n{{agency_name}}'}
          style={{ ...controlBox(body.length > BODY_MAX, saving), padding: '10px 11px', lineHeight: 1.6, resize: 'vertical', minHeight: '140px' }}
        />

        {found.length > 0 && (
          <div style={{ marginTop: '8px', fontSize: '0.68rem', color: C.sub, lineHeight: 1.7 }}>
            Uses:{' '}
            {found.map((v) => {
              const auto = autoVariables.includes(v);
              return (
                <span
                  key={v}
                  title={auto ? 'Filled in automatically' : 'The sender types this when sending'}
                  style={{ display: 'inline-block', margin: '0 4px 3px 0', padding: '1px 8px', borderRadius: '20px', fontWeight: 700, fontSize: '0.63rem', background: auto ? C.limeDim : 'rgba(245,158,11,0.12)', color: auto ? '#4a5e00' : '#b45309', border: `1px solid ${auto ? C.limeBorder : 'rgba(245,158,11,0.35)'}` }}
                >
                  {`{{${v}}}`}
                </span>
              );
            })}
            {asked.length > 0 && <div style={{ color: '#b45309', fontWeight: 600 }}>Amber ones are not filled automatically — the sender is asked for them.</div>}
          </div>
        )}
      </div>

      {/* files */}
      <div>
        <label style={FIELD_LABEL}>
          Files <span style={{ color: C.muted, textTransform: 'none', letterSpacing: 0, fontWeight: 600 }}>· {fileCount}/{MAX_FILES} · PDF, image or Word · up to {MAX_FILE_BYTES / 1024 / 1024} MB each</span>
        </label>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {keptExisting.map((a) => (
            <FileRow key={a.path} name={a.file_name} size={a.size} href={a.url} onRemove={() => setRemoved((r) => [...r, a.path])} disabled={saving} />
          ))}
          {newFiles.map((f, i) => (
            <FileRow key={`${f.name}-${i}`} name={f.name} size={f.size} isNew onRemove={() => setNewFiles((cur) => cur.filter((_, j) => j !== i))} disabled={saving} />
          ))}
        </div>

        <input ref={fileRef} type="file" accept={ACCEPT} multiple hidden onChange={(e) => addFiles(e.target.files)} />
        {fileCount < MAX_FILES && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={saving}
            style={{ marginTop: fileCount ? '7px' : 0, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', height: '38px', borderRadius: '8.3px', border: `1px dashed ${G.line}`, background: G.shellBg, color: C.teal, fontSize: '0.72rem', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}
          >
            <Upload size={13} /> Add files
          </button>
        )}
        <div style={{ marginTop: '6px', fontSize: '0.63rem', color: C.muted }}>Files are added to the message as download links. Removing one here never breaks proposals already sent.</div>
      </div>

      {/* active */}
      <button
        type="button"
        role="switch"
        aria-checked={isActive}
        onClick={() => setIsActive((v) => !v)}
        disabled={saving}
        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 11px', borderRadius: '8.3px', border: `1px solid ${G.line}`, background: G.shellBg, cursor: saving ? 'not-allowed' : 'pointer', textAlign: 'left' }}
      >
        <span style={{ width: '34px', height: '20px', borderRadius: '20px', padding: '2px', background: isActive ? C.teal : '#d1d5db', display: 'flex', alignItems: 'center', justifyContent: isActive ? 'flex-end' : 'flex-start', transition: 'background 0.2s', flexShrink: 0 }}>
          <span style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.25)' }} />
        </span>
        <span>
          <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: C.teal }}>{isActive ? 'Active' : 'Hidden'}</span>
          <span style={{ display: 'block', fontSize: '0.65rem', color: C.muted }}>{isActive ? 'Your team can send it from a lead.' : 'Not shown on the send form.'}</span>
        </span>
      </button>
    </ProposalDialog>
  );
};

const FileRow: React.FC<{ name: string; size?: number; href?: string; isNew?: boolean; onRemove: () => void; disabled: boolean }> = ({ name, size, href, isNew, onRemove, disabled }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 9px', border: `1px solid ${G.line}`, borderRadius: '8px', background: C.white }}>
    <Paperclip size={12} color={C.tealMid} style={{ flexShrink: 0 }} />
    {href ? (
      <a href={href} target="_blank" rel="noopener noreferrer" style={{ flex: 1, minWidth: 0, fontSize: '0.72rem', fontWeight: 600, color: C.teal, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {name}
      </a>
    ) : (
      <span style={{ flex: 1, minWidth: 0, fontSize: '0.72rem', fontWeight: 600, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
    )}
    {isNew && <span style={{ padding: '1px 7px', borderRadius: '20px', fontSize: '0.58rem', fontWeight: 800, background: C.limeDim, color: '#4a5e00', border: `1px solid ${C.limeBorder}` }}>NEW</span>}
    <span style={{ fontSize: '0.64rem', color: C.muted, whiteSpace: 'nowrap' }}>{fmtSize(size)}</span>
    <button
      type="button"
      onClick={onRemove}
      disabled={disabled}
      title="Remove file"
      style={{ width: '24px', height: '24px', borderRadius: '6px', border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.06)', color: '#c62828', cursor: disabled ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
    >
      <Trash2 size={11} />
    </button>
  </div>
);

export default TemplateFormModal;
